import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/Button';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';
import { SettingsCard } from './shared/SettingsCard';
import { SettingsHeader } from './shared/SettingsHeader';
import { SettingsInput } from './shared/SettingsInput';
import { SettingsRow } from './shared/SettingsRow';
import passwordIcon from '@/assets/images/svg/password_icon.svg';

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type PasswordFormValues = z.infer<typeof passwordSchema>;

export const PasswordSettings: React.FC = () => {
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = React.useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmit = async (data: PasswordFormValues) => {
    try {
      await api.users.updatePassword(data.currentPassword, data.newPassword, data.confirmPassword);

      addToast({
        title: 'Success',
        subtitle: 'Password updated successfully.',
        type: 'success',
      });
      setIsEditing(false);
      reset();
    } catch (error) {
      addToast({
        title: 'Update Failed',
        subtitle: getErrorMessage(error),
        type: 'error',
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    reset();
  };

  const description = "It's a good idea to use a strong password that you don't use anywhere else";

  if (!isEditing) {
    return (
      <SettingsRow
        icon={passwordIcon}
        title='Password'
        description={description}
        onModify={() => setIsEditing(true)}
      />
    );
  }

  return (
    <SettingsCard className='bg-gray-75'>
      <SettingsHeader icon={passwordIcon} title='Password' description={description} isEditing />

      <form onSubmit={handleSubmit(onSubmit)} className='mt-6 space-y-6 pl-0 md:pl-10'>
        <div>
          <SettingsInput
            type='password'
            label='Current Password'
            placeholder='Enter current password'
            autoComplete='current-password'
            {...register('currentPassword')}
          />
          {errors.currentPassword && (
            <p className='mt-1 text-xs text-red-500'>{errors.currentPassword.message}</p>
          )}
        </div>

        <div>
          <SettingsInput
            type='password'
            label='New Password'
            placeholder='Enter new password'
            autoComplete='new-password'
            {...register('newPassword')}
          />
          {errors.newPassword && (
            <p className='mt-1 text-xs text-red-500'>{errors.newPassword.message}</p>
          )}
          <p className='mt-2 font-sans text-12 text-gray-500'>Minimum 8 characters</p>
        </div>

        <div>
          <SettingsInput
            type='password'
            label='Confirm New Password'
            placeholder='Confirm new password'
            autoComplete='new-password'
            {...register('confirmPassword')}
          />
          {errors.confirmPassword && (
            <p className='mt-1 text-xs text-red-500'>{errors.confirmPassword.message}</p>
          )}
        </div>

        <div className='flex items-center justify-between pt-2'>
          <Button
            type='submit'
            variant='settings-modify'
            className='h-[36px] w-auto min-w-[154px] px-6'
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Updating...' : 'Update Password'}
          </Button>

          <button
            type='button'
            onClick={handleCancel}
            className='font-sans text-16 font-semibold text-black transition-opacity hover:opacity-70'
          >
            Cancel
          </button>
        </div>
      </form>
    </SettingsCard>
  );
};
